import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { REGION_LABELS, REGION_ORDER } from '../../constants/regions';
import { shareStory as nativeShare } from '../../mobile';

const StoryCard = ({ story, expanded, onToggle }) => {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    if (expanded && !comparison) {
      setLoading(true);
      axios.get(`${API_URL}/v1/stories/${story.cluster_id}/comparison`)
        .then(res => {
          if (isMounted) setComparison(res.data);
        })
        .catch(err => console.error('Failed to load comparison', err))
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }
    
    return () => { isMounted = false; };
  }, [expanded, story.cluster_id, comparison]);

  const handleShare = async () => {
    try {
      const res = await axios.post(`${API_URL}/v1/share/create`, {
        cluster_id: story.cluster_id
      });
      
      // Use native share on mobile, web share API, or clipboard fallback
      const shareResult = await nativeShare(story, res.data.share_url);
      
      if (shareResult.method === 'clipboard') {
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  return (
    <article className={`story-card ${expanded ? 'expanded' : ''}`} data-testid={`story-card-${story.cluster_id}`}>
      <div className="story-header" onClick={onToggle}>
        {/* Trending Badges */}
        <div className="story-badges">
          {story.is_breaking && <span className="badge breaking">BREAKING</span>}
          {story.has_high_profile && story.figures?.length > 0 && (
            <span className="badge high-profile">👤 {story.figures[0]}</span>
          )}
          {story.category && (
            <span className={`badge category-${story.category}`}>{story.category.toUpperCase()}</span>
          )}
        </div>
        <div className="story-meta">
          <span>{story.source_count} sources</span>
          <span>{story.regions?.length || 0} regions</span>
          {story.trending_score && (
            <span className="trending-score" title="Trending Score">🔥 {story.trending_score}</span>
          )}
        </div>
        <h2 className="story-title">{story.title}</h2>
        <div className="story-regions">
          {(story.regions || []).map(r => (
            <span key={r} className="region-badge">{REGION_LABELS[r] || r}</span>
          ))}
        </div>
      </div>
      
      {expanded && (
        <div className="story-comparison">
          {loading ? (
            <div className="loading"><p>Loading comparison...</p></div>
          ) : comparison ? (
            <>
              <div className="comparison-grid">
                {REGION_ORDER.map(region => {
                  const sources = comparison.regions[region] || [];
                  return (
                    <div key={region} className="region-col">
                      <div className="region-header">
                        <span>{REGION_LABELS[region]}</span>
                        <span className="count">{sources.length || '—'}</span>
                      </div>
                      <div className="region-body">
                        {sources.length > 0 ? sources.map((s, i) => (
                          <div key={i} className="source-item">
                            <div className="source-name">{s.source_name}</div>
                            <div className="source-headline">
                              <a href={s.url} target="_blank" rel="noopener noreferrer">{s.headline}</a>
                            </div>
                            {s.tags && s.tags.length > 0 && (
                              <div className="source-tags">
                                {s.tags.map(t => (
                                  <span key={t} className="tag">{t.replace('_', ' ')}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )) : (
                          <div className="no-coverage">No coverage</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="story-actions">
                <button className="action-btn" onClick={handleShare} data-testid="share-btn">
                  Share Comparison
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state"><p>Failed to load comparison</p></div>
          )}
        </div>
      )}
    </article>
  );
};

export default StoryCard;
